import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Artist } from '@/types';

async function fetchArtistsData(limit?: number): Promise<Artist[]> {
  let query = supabase
    .from('artists')
    .select('id, name, image, genre, slug, created_at')
    .eq('is_hidden', false)
    .order('created_at', { ascending: false });

  if (limit && limit > 0) query = query.limit(limit);

  const { data: artistsData, error: artistsError } = await query;

  if (artistsError) throw artistsError;

  const artistIds = (artistsData || []).map((a: any) => a.id);
  if (artistIds.length === 0) return [];

  // Batch in chunks of 100 to avoid URL length limits (400 errors)
  const CHUNK_SIZE = 100;
  const chunks: string[][] = [];
  for (let i = 0; i < artistIds.length; i += CHUNK_SIZE) {
    chunks.push(artistIds.slice(i, i + CHUNK_SIZE));
  }

  const [followersResults, reviewsResult, toursResults] = await Promise.all([
    Promise.all(chunks.map(chunk =>
      supabase.from('artist_followers').select('artist_id').in('artist_id', chunk)
    )),
    supabase.from('reviews').select('artist_name, rating'),
    Promise.all(chunks.map(chunk =>
      supabase.from('tours').select('artist_id, name').in('artist_id', chunk)
    )),
  ]);

  const followersResult = { data: followersResults.flatMap(r => r.data || []) };
  const toursResult = { data: toursResults.flatMap(r => r.data || []) };

  const followerMap: Record<string, number> = {};
  (followersResult.data || []).forEach((row: any) => {
    followerMap[row.artist_id] = (followerMap[row.artist_id] || 0) + 1;
  });

  // Pre-index reviews by lower-cased artist name and tours by artist id so the
  // per-artist loop below is O(1) instead of scanning every review/tour row for
  // each of the (up to 300) artists. Same inputs, same averages, same counts.
  const ratingsByArtistName = new Map<string, { sum: number; count: number }>();
  (reviewsResult.data || []).forEach((r: any) => {
    const key = String(r.artist_name).toLowerCase();
    const entry = ratingsByArtistName.get(key) || { sum: 0, count: 0 };
    entry.sum += Number(r.rating);
    entry.count += 1;
    ratingsByArtistName.set(key, entry);
  });

  const tourNamesByArtistId = new Map<string, Set<string>>();
  (toursResult.data || []).forEach((t: any) => {
    const set = tourNamesByArtistId.get(t.artist_id) || new Set<string>();
    set.add(t.name);
    tourNamesByArtistId.set(t.artist_id, set);
  });

  const formatted: Artist[] = (artistsData || []).map((a: any) => {
    const followerCount = followerMap[a.id] || 0;
    const agg = ratingsByArtistName.get(String(a.name).toLowerCase());
    const avg = agg && agg.count ? agg.sum / agg.count : 0;
    const uniqueTours = tourNamesByArtistId.get(a.id)?.size ?? 0;
    
    
    return {
      id: a.id,
      name: a.name,
      image: a.image || '',
      genre: a.genre || [],
      concertCount: uniqueTours,
      followers: followerCount,
      averageRating: Math.round(avg * 10) / 10,
      slug: a.slug || undefined,
    } as Artist;
  });

  return formatted;
}

export const useArtists = (options?: { limit?: number }) => {
  const limit = options?.limit;
  const { data: artists = [], isLoading: loading, error, refetch } = useQuery({
    queryKey: ['artists', 'list', limit ?? 'all'],
    queryFn: () => fetchArtistsData(limit),
    staleTime: 10 * 60 * 1000, // 10 minutes
    gcTime: 30 * 60 * 1000,
  });

  return { artists, loading, error: error?.message || null, refetch };
};

async function fetchSingleArtist(idOrSlug: string): Promise<(Artist & { slug?: string }) | null> {
  const isUUID = /^[0-9a-fA-F-]{36}$/.test(idOrSlug);
  const query = supabase.from('artists').select('id, name, image, genre, slug');
  const { data, error } = await (isUUID
    ? query.eq('id', idOrSlug).maybeSingle()
    : query.eq('slug', idOrSlug).maybeSingle());

  if (error) throw error;
  if (!data) return null;

  const [followersResult, reviewsResult, toursResult] = await Promise.all([
    supabase.from('artist_followers').select('id', { count: 'exact', head: true }).eq('artist_id', data.id),
    supabase.from('reviews').select('rating').eq('artist_name', data.name),
    supabase.from('tours').select('name').eq('artist_id', data.id),
  ]);

  const ratings = (reviewsResult.data || []).map((r: any) => Number(r.rating));
  const avg = ratings.length ? ratings.reduce((s: number, n: number) => s + n, 0) / ratings.length : 0;
  const uniqueTours = new Set((toursResult.data || []).map((t: any) => t.name)).size;

  return {
    id: data.id,
    name: data.name,
    image: data.image || '',
    genre: data.genre || [],
    concertCount: uniqueTours,
    followers: followersResult.count || 0,
    averageRating: Math.round(avg * 10) / 10,
    slug: (data as any).slug || undefined,
  };
}

export const useArtist = (idOrSlug: string) => {
  const { data: artist = null, isLoading: loading, error, refetch } = useQuery({
    queryKey: ['artists', idOrSlug],
    queryFn: () => fetchSingleArtist(idOrSlug),
    enabled: !!idOrSlug,
    staleTime: 10 * 60 * 1000,
  });

  return { artist, loading, error: error?.message || null, refetch };
};
